import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core';
import { AddCircleOutline } from '@material-ui/icons';

import IconButton from 'components/IconButton';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';

const useStyles = makeStyles((theme) => {
  const commonWhite = theme.palette.common.white;
  const shelfBlue500 = theme.palette.shelfBlue[500];
  return {
    /* Styles applied to the root element. */
    root: {
      margin: theme.spacing(1),
      width: `calc(100% - ${theme.spacing(2)}px)`,
    },
    options: {
      backgroundColor: commonWhite,
      padding: theme.spacing(0.75, 2),
      cursor: 'pointer',
      // Focused state styles
      '&.Mui-focused, &.Mui-focusVisible': {
        outline: 'none',
        backgroundColor: shelfBlue500,
        color: commonWhite,
        '& $icon': {
          color: commonWhite,
        },
        '& $text': {
          color: commonWhite,
        },
      },
    },
    icon: {
      color: theme.palette.primary.main,
    },
    text: {
      color: theme.palette.text.primary,
    },
    textFieldForSelect: {
      width: `calc(100% - ${theme.spacing(2)}px)`,
      '& input': {
        padding: theme.spacing(1.25),
      },
    },
  };
});

// Create filter options for autocomplete
const filter = createFilterOptions({
  stringify: (deck) => deck.userText,
});

const SelectFlashcardDeck = ({
  className,
  defaultValue = null,
  flashcardDecks = [],
  onSelect = () => {},
  onNewDeck = () => {},
  ...props
}) => {
  const classes = useStyles();
  const [value, setValue] = useState(defaultValue);
  const escapeKeyCount = useRef(0);

  const currentText = value?.userText ?? '';
  const handleOnChange = async (event, deck) => {
    if (!deck) {
      // No deck
      setValue(null);
      onSelect(null);
    } else if (deck.isNewOption) {
      // New deck
      const newDeck = await onNewDeck(deck.userText);
      setValue(newDeck);
    } else {
      // Existing deck
      setValue(deck);
      onSelect(deck);
    }
  };

  return (
    <Autocomplete
      aria-label="enter or select a deck"
      classes={{
        root: clsx(classes.root, className),
        input: 'noTranslate',
        option: classes.options,
      }}
      value={currentText}
      onChange={handleOnChange}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        const filteredText = filtered.map((option) => option.userText);
        // Suggest the creation of a new deck
        if (
          params.inputValue &&
          params.inputValue !== '' &&
          !filteredText.includes(params.inputValue)
        ) {
          filtered.push({
            userText: params.inputValue,
            isNewOption: true,
          });
        }

        return filtered;
      }}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      options={flashcardDecks}
      getOptionLabel={(option) => currentText}
      renderOption={(props, option) => {
        const { ...optionProps } = props;

        if (option?.isNewOption) {
          return (
            <li {...optionProps} key="new-deck" className={classes.options}>
              <IconButton aria-label="Add new Deck" className={classes.icon}>
                <AddCircleOutline fontSize="inherit" />
              </IconButton>
              <span className={classes.text}>
                Add <span className="noTranslate">{option.userText}</span>
              </span>
            </li>
          );
        }

        return (
          <li
            {...optionProps}
            key={option?.uuid}
            className={clsx(classes.options, 'noTranslate')}
          >
            {option?.userText ?? ''}
          </li>
        );
      }}
      onOpen={() => (escapeKeyCount.current = 0)}
      freeSolo
      id="select-flashcard-deck"
      renderInput={(params) => (
        <TextField
          id="choose-deck-label"
          aria-label="choose a deck"
          {...params}
          className={classes.textFieldForSelect}
          label="Choose a Deck"
          variant="outlined"
          onKeyUp={(e) => {
            if (escapeKeyCount.current < 1) {
              e.stopPropagation();
              e.preventDefault();
              if (e.key === 'Escape') {
                escapeKeyCount.current += 1;
              }
            }
          }}
        />
      )}
      {...props}
    />
  );
};

SelectFlashcardDeck.propTypes = {
  /**
   * Callback to run when a deck is selected.
   * The selected deck will be passed as an argument.
   */
  onSelect: PropTypes.func,
  /**
   * A default deck to start with.
   */
  defaultValue: PropTypes.object,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * @ignore
   */
  className: PropTypes.string,
  flashcardDecks: PropTypes.array,
};

export default SelectFlashcardDeck;
